import {
    AddsConnectionDetails,
    BackOfficeDeliveryIconConfig,
    BackOfficeSyncIconConfig,
    CarGasConnectionDetails,
    CargasDeliveryStatusRequest,
    CreateCargasDeliveryRequestFields,
    Device,
    RawAddsCustomerData,
    RawAddsTankData,
    RawCargasCustomerData,
    RawCargasTankData,
    SupplierInfo,
    SupportedBackOffices,
    ThirdPartyBackOfficeCustomerInfo,
    ThirdPartyBackOfficeTankInfo,
} from 'types';
import { backofficeFields } from 'lib/thirdPartyBackOfficeConstants';
import { getDateWithTime } from 'helpers/dates';
import { baseApi } from './api';
import {
    getFirebaseAddsBackoffice,
    getFirebaseCargasBackoffice,
    getFirebaseSupplierBackoffice,
    getSupplierInfo,
    removeFirebaseBackoffice,
    updateFirebaseBackoffice,
    updateSupplierInfo,
} from 'lib/firebase';
import { updateAuditLog } from './audit';
import { StoredUser } from 'store/features/authentication';

/** RTK queries to interact with napi endpoints that support integration with third-party backoffice suites. */
export const thirdPartyBackofficeApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getCargasDeliveryStatus: builder.query<
            CargasDeliveryStatusRequest,
            { supplierId: string; tankNumber: string }
        >({
            query: ({ supplierId, tankNumber }) => {
                if (!supplierId) throw new Error('supplierId is required.');
                if (!tankNumber) throw new Error('tankNumber is required.');

                return {
                    method: 'POST',
                    url: `cargas/${supplierId}/tank`,
                    body: {
                        tankId: tankNumber,
                    },
                };
            },
        }),
        cargasSyncTank: builder.query<
            ThirdPartyBackOfficeTankInfo,
            { supplierId: string; tankNumber: string }
        >({
            query: ({ supplierId, tankNumber }) => {
                if (!supplierId) throw new Error('supplierId is required.');
                if (!tankNumber) throw new Error('tankNumber is required.');

                return {
                    method: 'POST',
                    url: `cargas/${supplierId}/tank`,
                    body: {
                        tankId: tankNumber,
                    },
                };
            },
            transformResponse: (res: RawCargasTankData) => {
                const loc = res.loc;
                const processedRes: ThirdPartyBackOfficeTankInfo = {
                    address:
                        loc.Address1.trim() +
                        ', ' +
                        loc.City.trim() +
                        ', ' +
                        loc.State.trim() +
                        ' ' +
                        loc.Zipcode.trim(),
                    tank_name: res.tank.bEquipmentDescription ?? '',
                    capacity: parseFloat(res.tank.TankSize),
                    notes: '',
                    group: loc.DeliveryZone ?? loc.ServiceZone ?? '',
                    delivery_type: res.tank.DeliveryType ?? '',
                };

                return processedRes;
            },
        }),
        cargasSyncCustomer: builder.query<
            ThirdPartyBackOfficeCustomerInfo,
            { supplierId: string; customerId: string }
        >({
            query: ({ supplierId, customerId }) => {
                if (!supplierId) throw new Error('supplierId is required.');
                if (!customerId) throw new Error('customerId is required.');

                return {
                    method: 'POST',
                    url: `cargas/${supplierId}/customer`,
                    body: {
                        customerId,
                    },
                };
            },
            transformResponse: (res: RawCargasCustomerData) => {
                const processedRes: ThirdPartyBackOfficeCustomerInfo = {
                    customer_name: res.FullName ?? '',
                };
                return processedRes;
            },
        }),
        addsSyncTank: builder.query<
            ThirdPartyBackOfficeTankInfo,
            { supplierId: string; tankNumber: string; customerId: string }
        >({
            query: ({ supplierId, tankNumber, customerId }) => {
                if (!supplierId) throw new Error('supplierId is required.');
                if (!tankNumber) throw new Error('tankNumber is required.');
                if (!customerId) throw new Error('customerId is required.');

                return {
                    method: 'POST',
                    url: `adds/${supplierId}/tank`,
                    body: {
                        tankId: tankNumber,
                        customerId,
                    },
                };
            },
            transformResponse: (res: RawAddsTankData) => {
                const processedRes: ThirdPartyBackOfficeTankInfo = {
                    address: res.delivery_address ?? '',
                    tank_name: '',
                    capacity: parseFloat(res.size),
                    notes: '',
                };
                return processedRes;
            },
        }),
        addsSyncCustomer: builder.query<
            ThirdPartyBackOfficeCustomerInfo,
            { supplierId: string; customerId: string }
        >({
            query: ({ supplierId, customerId }) => {
                if (!supplierId) throw new Error('supplierId is required.');
                if (!customerId) throw new Error('customerId is required.');

                return {
                    method: 'POST',
                    url: `adds/${supplierId}/customer`,
                    body: {
                        customerId,
                    },
                };
            },
            transformResponse: (res: RawAddsCustomerData) => {
                const processedRes: ThirdPartyBackOfficeCustomerInfo = {
                    customer_name: res.name ?? '',
                };
                return processedRes;
            },
        }),
        /** Schedule a delivery in the cargas third-party backoffice for a given tank. */
        createCargasBackOfficeDelivery: builder.query<
            void,
            {
                supplierId: string;
                tankNumber: string;
                deliveryDate: string;
                ticketMessage: string;
            }
        >({
            query: ({
                supplierId,
                tankNumber,
                deliveryDate,
                ticketMessage,
            }) => {
                if (!supplierId) throw new Error('supplierId is required.');
                if (!tankNumber) throw new Error('tankNumber is required.');
                if (!deliveryDate) throw new Error('deliveryDate is required.');
                if (!ticketMessage)
                    throw new Error('ticketMessage is required.');

                const body: CreateCargasDeliveryRequestFields = {
                    UserName: 'Generac Fuel Scheduler',
                    TankID: tankNumber,
                    DeliveryDate: deliveryDate,
                    DueDate: deliveryDate,
                    TicketMessage: ticketMessage,
                };

                return {
                    method: 'POST',
                    url: `cargas/${supplierId}/create-delivery`,
                    body,
                };
            },
        }),
        addsTest: builder.query<any, { supplierId: string }>({
            query: ({ supplierId }) => {
                if (!supplierId) throw new Error('supplierId is required.');

                return {
                    method: 'GET',
                    url: `/adds/${supplierId}/test`,
                };
            },
        }),
        cargasTest: builder.query<any, { supplierId: string }>({
            query: ({ supplierId }) => {
                if (!supplierId) throw new Error('supplierId is required.');

                return {
                    method: 'GET',
                    url: `/cargas/${supplierId}/test`,
                };
            },
        }),
    }),
});

export const {
    useLazyGetCargasDeliveryStatusQuery,
    useLazyAddsSyncCustomerQuery,
    useLazyAddsSyncTankQuery,
    useLazyCargasSyncCustomerQuery,
    useLazyCargasSyncTankQuery,
    useLazyCreateCargasBackOfficeDeliveryQuery,
    useLazyAddsTestQuery,
    useLazyCargasTestQuery,
} = thirdPartyBackofficeApi;

/** Get a customer-friendly display label for the given backoffice. */
export const getBackOfficeLabel = (
    backoffice: SupportedBackOffices
): string => {
    let label = '';
    if (backofficeFields[backoffice] && backofficeFields[backoffice].label)
        label = backofficeFields[backoffice].label;
    return label;
};

/** Get the label for the backoffice sync page 'account number' field based on the specific terminology used by the given backoffice. */
export const getBackOfficeAccountNumberLabel = (
    backoffice: SupportedBackOffices
): string => {
    let label = '';
    if (
        backofficeFields[backoffice] &&
        backofficeFields[backoffice].account_number
    )
        label = backofficeFields[backoffice].account_number;
    return label;
};

/** Get the label for the backoffice sync page 'tank number' field based on the specific terminology used by the given backoffice. */
export const getBackOfficeTankNumberLabel = (
    backoffice: SupportedBackOffices
): string => {
    let label = '';
    if (
        backofficeFields[backoffice] &&
        backofficeFields[backoffice].tank_number
    )
        label = backofficeFields[backoffice].tank_number;
    return label;
};

/** Get the backoffice delivery icon configuration based on device input. */
export const getBackOfficeDeliveryIcon = (
    device: Device
): BackOfficeDeliveryIconConfig => {
    // Initialize at default settings as if backoffice delivery settings are not configured.
    const iconConfig: BackOfficeDeliveryIconConfig = {
        delivery_icon: 'block',
        delivery_style:
            'fill: #909090; stroke-width: .5; stroke:#A9A9A9;margin-top: 8.5;margin-bottom: 8.5;',
        bo_delivery_msg: 'Not Configured',
        color: '#909090',
    };

    if (device._bo_sync_complete) {
        iconConfig.delivery_icon = 'add';
        iconConfig.delivery_style =
            'fill: #F19436; stroke-width: .5; stroke:#A9A9A9;margin-top: 8.5;margin-bottom: 8.5;';
        iconConfig.bo_delivery_msg = 'Delivery Not Scheduled';
        iconConfig.color = '#F19436';

        if (device._has_delivery) {
            iconConfig.delivery_icon = 'done';
            iconConfig.delivery_style =
                'fill: #55A05C; stroke-width: .5; stroke:#A9A9A9;margin-top: 8.5;margin-bottom: 8.5;';
            iconConfig.bo_delivery_msg = 'Delivery Scheduled';
            iconConfig.color = '#55A05C';
        }
    }

    return iconConfig;
};

/** Get the backoffice sync icon configuration based on device input. */
export const getBackOfficeSyncIcon = (
    device: Device
): BackOfficeSyncIconConfig => {
    const now = new Date().getTime();

    let iconConfig: BackOfficeSyncIconConfig = {
        backoffice_icon: '',
        backoffice_style: '',
        bo_sync_msg: device.bo_sync_msg ?? '',
        bo_sync_complete: false,
        color: '#909090',
    };

    if (!device.accountId || !device.capacity) {
        iconConfig = setSyncIconMissingDeviceInfo(device, iconConfig);
    }
    // If missing both time and msg, likely not set up.
    else if (!device.bo_sync_msg && !device.bo_sync_time) {
        iconConfig = setSyncIconBoSyncNotSetUp(iconConfig);
    }
    // Checking if sync time in last 3 days.
    else if (device.bo_sync_time && now - device.bo_sync_time < 86400000 * 3) {
        iconConfig = setSyncIcon(device, iconConfig);
    }
    // No sync time or old sync time, likely a problem.
    else {
        iconConfig = setSyncIconSyncFailed(device, iconConfig);
    }

    return iconConfig;
};

const setSyncIconMissingDeviceInfo = (
    device: Device,
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    iconConfig.backoffice_icon = 'sync_disabled';
    iconConfig = setSyncIconGray(iconConfig);
    iconConfig.bo_sync_msg = 'No ';

    // Build up bo_sync_msg to display helpful message to user about what device fields are missing.
    const msg = [];
    if (!device.accountId) {
        msg.push('account ID');
    }
    if (!device.capacity) {
        msg.push('capacity');
    }

    iconConfig.bo_sync_msg += msg.join(' or ') + ' specified';

    return iconConfig;
};

const setSyncIconBoSyncNotSetUp = (
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    iconConfig.backoffice_icon = 'sync_disabled';
    iconConfig.bo_sync_msg = 'Sync Disabled';
    iconConfig = setSyncIconGray(iconConfig);

    return iconConfig;
};

const setSyncIconSyncFailed = (
    device: Device,
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    iconConfig = setSyncProblemIcon(iconConfig);

    if (!device.bo_sync_msg || device.bo_sync_msg === 'ftp_sync') {
        if (device.bo_sync_time) {
            iconConfig.bo_sync_msg =
                'Last sync: ' + getDateWithTime(device.bo_sync_time);
        } else {
            iconConfig.bo_sync_msg = 'Sync failed';
        }
    }

    return iconConfig;
};

const setSyncProblemIcon = (
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    iconConfig.backoffice_icon = 'sync_problem';
    iconConfig = setSyncIconRed(iconConfig);

    return iconConfig;
};

const setSyncIcon = (
    device: Device,
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    // If no msg, things are likely good.
    if (!device.bo_sync_msg && device.bo_sync_time) {
        iconConfig.backoffice_icon = 'sync';
        iconConfig.bo_sync_msg =
            'Last sync: ' + getDateWithTime(device.bo_sync_time);
        iconConfig.bo_sync_complete = true;
        iconConfig = setSyncIconGreen(iconConfig);
    } else if (device.bo_sync_msg === 'Not monitored' && device.bo_sync_time) {
        iconConfig.backoffice_icon = 'sync';
        iconConfig.bo_sync_msg =
            'Last sync: ' +
            getDateWithTime(device.bo_sync_time) +
            ' : Not Monitored';
        iconConfig.bo_sync_complete = true;
        iconConfig = setSyncIconOrange(iconConfig);
    }
    // Only ftp should set a time and msg.
    else if (device.bo_sync_msg === 'ftp_sync') {
        iconConfig = setSyncIconFtpSync(device, iconConfig);
    } else {
        iconConfig = setSyncProblemIcon(iconConfig);
    }
    return iconConfig;
};

const setSyncIconFtpSync = (
    device: Device,
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    if (device.bo_sync_time) {
        iconConfig.backoffice_icon = 'description';
        iconConfig.bo_sync_msg =
            'Last FTP file created: ' + getDateWithTime(device.bo_sync_time);
        iconConfig.bo_sync_complete = true;
        iconConfig = setSyncIconGreen(iconConfig);
    }

    return iconConfig;
};

const setSyncIconGray = (
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    iconConfig.backoffice_style =
        'fill: #909090; stroke-width: .5; stroke:#A9A9A9;margin-top: 8.5;margin-bottom: 8.5;';
    iconConfig.color = '#909090';

    return iconConfig;
};

const setSyncIconGreen = (
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    iconConfig.backoffice_style =
        'fill: #55A05C; stroke-width: .5; stroke:#A9A9A9;margin-top: 8.5;margin-bottom: 8.5;';
    iconConfig.color = '#55A05C';

    return iconConfig;
};

const setSyncIconOrange = (
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    iconConfig.backoffice_style =
        'fill: #F19436; stroke-width: .5; stroke:#A9A9A9;margin-top: 8.5;margin-bottom: 8.5;';
    iconConfig.color = '#F19436';

    return iconConfig;
};

const setSyncIconRed = (
    iconConfig: BackOfficeSyncIconConfig
): BackOfficeSyncIconConfig => {
    iconConfig.backoffice_style =
        'fill: #EC4E27; stroke-width: .5; stroke:#A9A9A9;margin-top: 8.5;margin-bottom: 8.5;';
    iconConfig.color = '#EC4E27';

    return iconConfig;
};

/** Parse out and return the backoffice account number from an accountId (accountId is formatted as `${accountNumber}-${tankNumber}`). */
export const getBackOfficeAccountNumber = (accountId: string): string => {
    let accountNumber = '';

    if (!accountId) return accountNumber;

    const accountIdParts: string[] = accountId.split('-');

    if (accountIdParts.length > 1) accountNumber = accountIdParts[0];
    else accountNumber = accountId;

    return accountNumber;
};

/** Parse out and return the backoffice tank number from an accountId (accountId is formatted as `${accountNumber}-${tankNumber}`). */
export const getBackOfficeTankNumber = (accountId: string): string => {
    let tankNumber = '';

    if (!accountId) return tankNumber;

    const accountIdParts: string[] = accountId.split('-');

    if (accountIdParts.length > 1) tankNumber = accountIdParts[1];

    return tankNumber;
};

/** Get supplier's third-party backoffice data (use getCargasBackoffice for cargas and getAddsBackoffice for adds though as those have specialized return types.). */
export const getSupplierBackoffice = async (
    supplierId: string,
    backofficeName: SupportedBackOffices
) => {
    return getFirebaseSupplierBackoffice(supplierId, backofficeName);
};

/** Get backoffice configuration for ADDs third-party backoffice. */
export const getAddsBackoffice = async (
    supplierId: string
): Promise<AddsConnectionDetails> => {
    return getFirebaseAddsBackoffice(supplierId);
};

/** Get backoffice configuration for CarGas third-party backoffice. */
export const getCargasBackoffice = async (
    supplierId: string
): Promise<CarGasConnectionDetails> => {
    return getFirebaseCargasBackoffice(supplierId);
};

/** Delete the existing backoffice entry for the supplier. */
export const removeBackoffice = async (
    supplierId: string,
    oldBackofficeName: SupportedBackOffices
): Promise<void> => {
    return removeFirebaseBackoffice(supplierId, oldBackofficeName);
};

/** Save third-party backoffice data for supplier. Includes updating the audit log.*/
export const saveBackoffice = async (
    supplierId: string,
    supplierInfo: SupplierInfo,
    user: StoredUser,
    newBackoffice: SupportedBackOffices,
    newBackofficeData: any
): Promise<void> => {
    const supplierInfoFetch = await getSupplierInfo(supplierId);
    const oldBackoffice = supplierInfoFetch.backoffice;
    if (newBackoffice !== oldBackoffice) {
        getSupplierBackoffice(supplierId, oldBackoffice).then(
            (oldBackofficeData) => {
                updateAuditLog(
                    supplierId,
                    user,
                    'backoffice',
                    oldBackofficeData,
                    newBackofficeData
                ).then(() => {
                    removeBackoffice(supplierId, oldBackoffice).then(() => {
                        if (newBackoffice !== 'none') {
                            updateFirebaseBackoffice(
                                supplierId,
                                newBackoffice,
                                newBackofficeData,
                                supplierInfo.ftp_username
                            ).then(() => {
                                updateSupplierInfo(supplierId, {
                                    backoffice: newBackoffice,
                                });
                            });
                        }
                    });
                });
            }
        );
    } else if (newBackoffice !== 'none') {
        updateFirebaseBackoffice(
            supplierId,
            newBackoffice,
            newBackofficeData,
            supplierInfo.ftp_username
        );
    }
};
